import { createRootRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useLogout, useMe } from '../hooks/useAuth'
import { useSearch } from '../hooks/useSearch'
import { hasTokens } from '../lib/auth'
import { canTriggerScrape, canViewKpi } from '../lib/permissions'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { NotificationsBell } from '../components/NotificationsBell'
import { MessagesInbox } from '../components/MessagesInbox'
import { ChangePasswordModal } from '../components/ChangePasswordModal'
import { ToastProvider } from '../components/Toast'
import { useConversations } from '../hooks/useConversations'
import { useNotifications } from '../hooks/useNotifications'

export const Route = createRootRoute({
  component: RootLayout,
})

function RootLayout() {
  // Unauthenticated → render bare Outlet (lets /login render plainly).
  if (!hasTokens()) {
    return (
      <div className="center-page">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </div>
    )
  }
  return (
    <ToastProvider>
      <AuthedShell />
    </ToastProvider>
  )
}

// Compute hue 0-360 from string hash for avatar background.
// Matches mockup convention: oklch(58% 0.12 H).
function avatarHue(name: string): number {
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360
  return h
}

function initials(name: string, surname: string | null): string {
  const a = (name?.[0] ?? '').toUpperCase()
  const b = (surname?.[0] ?? '').toUpperCase()
  return (a + b) || a || '?'
}

function AuthedShell() {
  const navigate = useNavigate()
  const logout = useLogout()
  const { data } = useMe()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // Badge "Messaggi" nel menu = somma chat non lette + notifiche non lette
  // (stesso conteggio mostrato nel campanello / pagina /messaggi).
  const convsQ = useConversations()
  const convUnread = (convsQ.data?.items ?? []).reduce(
    (n, c) => n + (c.unread_count || 0),
    0,
  )
  const notifsQ = useNotifications(false, 1, 'all')
  const notifUnread = notifsQ.data?.unread_count ?? 0
  const messagesUnreadCount = convUnread + notifUnread

  function onLogout() {
    logout()
    void navigate({ to: '/login' })
  }

  const fullName = data
    ? data.surname
      ? `${data.name} ${data.surname}`
      : data.name
    : ''

  const avatarSeed = data ? `${data.name}${data.surname ?? ''}` : 'MB'
  const avatarBg = `oklch(58% 0.12 ${avatarHue(avatarSeed)})`

  return (
    <div className="app-shell" data-mobile-nav={mobileNavOpen ? 'open' : 'closed'}>
      <header className="topbar">
        <button
          type="button"
          className="hamburger"
          onClick={() => setMobileNavOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={mobileNavOpen}
        >
          ☰
        </button>

        <Link to="/" className="brand" aria-label="MB Aste — Dashboard">
          <div className="brand-mark">MB</div>
          <div>
            MB Aste <small>v1</small>
          </div>
        </Link>

        <GlobalSearch />

        <div className="spacer" />

        <div className="topbar-actions">
          <MessagesInbox />
          <NotificationsBell />
          <div className="divider-v" />
          {data && (
            <UserMenu
              fullName={fullName}
              email={data.email}
              role={data.role}
              title={data.title}
              initials={initials(data.name, data.surname)}
              avatarBg={avatarBg}
              onProfile={() => void navigate({ to: '/profile', search: {} as never })}
              onLogout={onLogout}
            />
          )}
        </div>
      </header>

      <aside className="sidebar">
        <nav onClick={() => setMobileNavOpen(false)}>
          <div className="nav-group-title">Operativo</div>
          <Link
            to="/"
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
            activeOptions={{ exact: true }}
          >
            <NavIcon name="dashboard" />
            <span>Dashboard</span>
          </Link>
          <Link
            to="/clienti"
            search={{ page: 1, page_size: 50, q: '', sort: '-created_at' }}
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="clienti" />
            <span>Clienti</span>
          </Link>
          <Link
            to="/lotti"
            search={{ page: 1, page_size: 50, q: '', sort: '-data_asta' }}
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="lotti" />
            <span>Lotti</span>
          </Link>
          <Link
            to="/match"
            search={{} as never}
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="partecipazioni" />
            <span>Match</span>
          </Link>
          <Link
            to="/sopralluoghi"
            search={{ page: 1, page_size: 50, q: '', sort: '-scheduled_at' }}
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="sopralluoghi" />
            <span>Sopralluoghi</span>
          </Link>
          <Link
            to="/scd-board"
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="pipeline" />
            <span>SCD Fattibilità</span>
          </Link>
          <Link
            to="/professionals"
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="professionisti" />
            <span>Professionisti</span>
          </Link>
          <Link
            to="/partecipazioni"
            search={
              {
                page: 1,
                page_size: 50,
                q: '',
                sort: '-created_at',
              } as never
            }
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="partecipazioni" />
            <span>Partecipazioni</span>
          </Link>
          <Link
            to="/pipeline/post-agg"
            search={{} as never}
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="pipeline" />
            <span>Post-Aggiudicazione</span>
          </Link>
          {canViewKpi(data) && (
            <Link
              to="/kpi"
              search={{} as never}
              className="nav-link"
              activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
            >
              <NavIcon name="kpi" />
              <span>KPI</span>
            </Link>
          )}

          <div className="nav-group-title">Comunicazione</div>
          <Link
            to="/messaggi"
            search={{} as never}
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="messaggi" />
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Messaggi
              {messagesUnreadCount > 0 && (
                <span
                  aria-label={`${messagesUnreadCount} tra chat e notifiche non lette`}
                  style={{
                    display: 'inline-grid',
                    placeItems: 'center',
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: 'var(--risk)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  {messagesUnreadCount > 99 ? '99+' : messagesUnreadCount}
                </span>
              )}
            </span>
          </Link>

          <div className="nav-group-title">Admin</div>
          {canTriggerScrape(data) && (
            <Link
              to="/admin/scrape"
              search={{} as never}
              className="nav-link"
              activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
            >
              <NavIcon name="sorgenti" />
              <span>Sorgenti</span>
            </Link>
          )}
          <Link
            to="/profile"
            search={{} as never}
            className="nav-link"
            activeProps={{ className: 'nav-link active', 'aria-current': 'page' }}
          >
            <NavIcon name="profilo" />
            <span>Profilo</span>
          </Link>
        </nav>

        <div className="sidebar-footer">
          <div className="sync-line">
            <span className="pulse-dot" />
            <span>Astegiudiziarie · sync attivo</span>
          </div>
          <div className="sync-line">
            <span className="pulse-dot" />
            <span>PVP · sync attivo</span>
          </div>
          <div className="sync-line">
            <span className="pulse-dot warn" />
            <span>Quimmo · in attesa</span>
          </div>
          <div className="sync-line">
            <span className="pulse-dot" />
            <span>Avvisi Notarili · oggi</span>
          </div>
        </div>
      </aside>

      <div
        className="sidebar-backdrop"
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />

      <main className="content">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {data?.must_change_password && (
        <ChangePasswordModal open={true} forced={true} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sidebar nav icons (line, 1.7 stroke, currentColor)
// ---------------------------------------------------------------------------

type IconName =
  | 'dashboard'
  | 'clienti'
  | 'lotti'
  | 'sopralluoghi'
  | 'partecipazioni'
  | 'pipeline'
  | 'kpi'
  | 'messaggi'
  | 'sorgenti'
  | 'profilo'
  | 'professionisti'

function UserMenu({
  fullName,
  email,
  role,
  initials: ini,
  avatarBg,
  title,
  onProfile,
  onLogout,
}: {
  fullName: string
  email: string
  role: string
  title?: string | null
  initials: string
  avatarBg: string
  onProfile: () => void
  onLogout: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const roleLabel: Record<string, string> = {
    supervisore: 'Supervisore',
    analista: 'Analista',
    responsabile: 'Responsabile',
    lead_manager: 'Lead Manager',
    consulente: 'Consulente',
    consulente_ex: 'Consulente (ex)',
    lead_manager_ex: 'Lead Manager (ex)',
    broker_mutui: 'Broker mutui',
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-pill"
        onClick={() => setOpen((v) => !v)}
        title="Menu utente"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="avatar" style={{ background: avatarBg }}>
          {ini}
        </div>
        <div className="user-pill-text">
          <div className="user-name">{fullName}</div>
          <div className="user-role">
            {title || roleLabel[role] || role}
          </div>
        </div>
        <span aria-hidden style={{ marginLeft: 6, fontSize: 10, color: 'var(--ink-3)' }}>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="user-menu-pop"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            zIndex: 60,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
            minWidth: 260,
            padding: 6,
            fontSize: 'var(--fs-13)',
          }}
        >
          {/* Header card */}
          <div
            style={{
              padding: 'var(--sp-3)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--bg-2)',
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
            }}
          >
            <div
              className="avatar lg"
              style={{ background: avatarBg, width: 40, height: 40 }}
            >
              {ini}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontWeight: 'var(--fw-semibold)',
                  fontSize: 'var(--fs-13)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fullName}
              </div>
              <div
                className="muted"
                style={{
                  fontSize: 'var(--fs-11)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {email}
              </div>
              <div
                style={{
                  marginTop: 4,
                  display: 'inline-block',
                  fontSize: 9,
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  fontWeight: 'var(--fw-semibold)',
                  color: 'var(--accent-ink)',
                  background: 'var(--accent-soft)',
                  padding: '1px 6px',
                  borderRadius: 'var(--r-pill)',
                }}
              >
                {roleLabel[role] ?? role}
              </div>
            </div>
          </div>
          {/* Items */}
          <UserMenuItem
            icon="👤"
            label="Profilo"
            onClick={() => {
              setOpen(false)
              onProfile()
            }}
          />
          <UserMenuItem
            icon="🔑"
            label="Cambia password"
            onClick={() => {
              setOpen(false)
              onProfile()
            }}
          />
          <div
            style={{
              height: 1,
              background: 'var(--line)',
              margin: '4px 8px',
            }}
          />
          <UserMenuItem
            icon="🚪"
            label="Esci"
            onClick={() => {
              setOpen(false)
              onLogout()
            }}
            danger
          />
        </div>
      )}
    </div>
  )
}

function UserMenuItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: string
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '8px 12px',
        borderRadius: 'var(--r-sm)',
        background: 'transparent',
        border: 'none',
        textAlign: 'left',
        color: danger ? 'var(--risk-ink, #b91c1c)' : 'var(--ink-1)',
        cursor: 'pointer',
        fontSize: 'var(--fs-13)',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-2)'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <span aria-hidden style={{ width: 18, textAlign: 'center' }}>
        {icon}
      </span>
      {label}
    </button>
  )
}

function NavIcon({ name }: { name: IconName }) {
  const common = {
    className: 'ico',
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (name) {
    case 'dashboard':
      return (
        <svg {...common}>
          <path d="M3 11 12 4l9 7" />
          <path d="M5 10v10h14V10" />
        </svg>
      )
    case 'clienti':
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6" />
          <path d="M17 11a3 3 0 1 0 0-6" />
          <path d="M22 19c0-2.5-2-4.5-5-4.5" />
        </svg>
      )
    case 'lotti':
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="1" />
          <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" />
        </svg>
      )
    case 'sopralluoghi':
      return (
        <svg {...common}>
          <path d="M3 7h4l2-3h6l2 3h4v12H3z" />
          <circle cx="12" cy="13" r="3.5" />
        </svg>
      )
    case 'partecipazioni':
      return (
        <svg {...common}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <path d="M8 13h8M8 17h5" />
        </svg>
      )
    case 'pipeline':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="13" rx="1" />
          <rect x="14" y="3" width="7" height="18" rx="1" />
          <rect x="3" y="18" width="7" height="3" rx="1" />
        </svg>
      )
    case 'kpi':
      return (
        <svg {...common}>
          <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
        </svg>
      )
    case 'messaggi':
      return (
        <svg {...common}>
          <path d="M21 11.5a8.4 8.4 0 0 1-1 4.1 8.5 8.5 0 0 1-7.6 4.6 8.4 8.4 0 0 1-4.1-1L3 21l1.9-5.2A8.5 8.5 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />
        </svg>
      )
    case 'sorgenti':
      return (
        <svg {...common}>
          <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
          <path d="M3 12h4l3-7 4 14 3-7h4" />
        </svg>
      )
    case 'profilo':
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
      )
    case 'professionisti':
      return (
        <svg {...common}>
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
          <line x1="12" y1="12" x2="12" y2="16" />
          <line x1="10" y1="14" x2="14" y2="14" />
        </svg>
      )
  }
}

// ---------------------------------------------------------------------------
// Global search input + dropdown panel
// ---------------------------------------------------------------------------

function GlobalSearch() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [focused, setFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Debounce input → debouncedQ (600ms): lascia finire di scrivere (es. codici
  // lunghi) senza cercare a ogni tasto. Enter cerca subito (vedi onKeyDown).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 600)
    return () => clearTimeout(t)
  }, [q])

  const { data } = useSearch(debouncedQ, 5)

  // Click-outside handler.
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setFocused(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  // ⌘K / Ctrl+K keyboard shortcut to focus search.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMod = e.metaKey || e.ctrlKey
      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (inputRef.current) {
          inputRef.current.focus()
          inputRef.current.select()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  function close() {
    setFocused(false)
  }

  function clearAndClose() {
    setQ('')
    setDebouncedQ('')
    close()
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      clearAndClose()
      ;(e.target as HTMLInputElement).blur()
    } else if (e.key === 'Enter') {
      // Cerca subito senza aspettare il debounce.
      setDebouncedQ(q)
    }
  }

  function goToClient(id: string) {
    void navigate({ to: '/clienti/$id', params: { id } } as never)
    clearAndClose()
  }

  function goToProperty(id: string) {
    void navigate({ to: '/lotti/$id', params: { id } } as never)
    clearAndClose()
  }

  function goToParticipation(id: string) {
    void navigate({ to: '/partecipazioni/$id', params: { id } } as never)
    clearAndClose()
  }

  const trimmed = debouncedQ.trim()
  const showDropdown = focused && trimmed.length >= 2 && !!data
  const isEmpty =
    !!data &&
    data.clients.length === 0 &&
    data.properties.length === 0 &&
    data.participations.length === 0

  return (
    <div className="global-search" ref={containerRef}>
      <span className="icon-search" aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.7}
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="text"
        value={q}
        placeholder="Cerca cliente, lotto, partecipazione…"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={onKeyDown}
        aria-label="Ricerca globale (⌘K)"
      />
      <kbd>⌘K</kbd>
      {showDropdown && (
        <div className="search-dropdown">
          {isEmpty ? (
            <div className="search-empty">Nessun risultato</div>
          ) : (
            <>
              {data.clients.length > 0 && (
                <div className="search-section">
                  <div className="search-section-title">Clienti</div>
                  {data.clients.map((c) => (
                    <div
                      key={`c-${c.id}`}
                      className="search-hit"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        goToClient(c.id)
                      }}
                    >
                      {c.cognome} {c.nome}{' '}
                      <span className="role-pill">{c.priorita}</span>
                    </div>
                  ))}
                </div>
              )}
              {data.properties.length > 0 && (
                <div className="search-section">
                  <div className="search-section-title">Lotti</div>
                  {data.properties.map((p) => {
                    const detailParts: string[] = []
                    if (p.rge) detailParts.push(`RGE ${p.rge}`)
                    if (p.lotto) detailParts.push(`Lotto ${p.lotto}`)
                    if (p.piano) detailParts.push(`${p.piano}° piano`)
                    if (p.superficie_mq != null)
                      detailParts.push(`${p.superficie_mq}m²`)
                    if (p.tribunale) detailParts.push(p.tribunale)
                    const detail = detailParts.join(' · ')
                    return (
                      <div
                        key={`p-${p.id}`}
                        className="search-hit"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          goToProperty(p.id)
                        }}
                      >
                        <div>
                          {p.comune ?? '—'}
                          {p.address ? ` — ${p.address}` : ''}
                          {p.prezzo_base !== null
                            ? ` (€${Math.round(p.prezzo_base).toLocaleString('it-IT')})`
                            : ''}
                        </div>
                        {detail && (
                          <div className="muted" style={{ fontSize: '0.8rem' }}>
                            {detail}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
              {data.participations.length > 0 && (
                <div className="search-section">
                  <div className="search-section-title">Partecipazioni</div>
                  {data.participations.map((pa) => (
                    <div
                      key={`pa-${pa.id}`}
                      className="search-hit"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        goToParticipation(pa.id)
                      }}
                    >
                      {pa.client_name ?? '—'} →{' '}
                      {pa.property_address ?? pa.property_comune ?? '—'} (
                      {pa.status})
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
